import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { AiChatbot } from './shared/components/ai-chatbot/ai-chatbot';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AuthService } from './core/services/Auth/auth-service';
import { TimesheetStatusService } from './core/services/timesheet-status/timesheet-status.service';
import { NgSelectDropdownScrollGuard } from './core/services/ng-select-dropdown-scroll-guard/ng-select-dropdown-scroll-guard.service';
import { NgSelectSearchFocusService } from './core/services/ng-select-search-focus/ng-select-search-focus.service';
import {
  consumeEncodedUpdateDeepLinkFix,
  consumePendingEncodedUpdateDeepLink,
} from './core/utils/legacy-workluge-url.util';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, AiChatbot],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  protected title = 'workluge_frontend_v2';
  private routerSubscription!: Subscription;
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly timesheetStatusService = inject(TimesheetStatusService);
  private readonly ngSelectDropdownScrollGuard = inject(NgSelectDropdownScrollGuard);
  private readonly ngSelectSearchFocusService = inject(NgSelectSearchFocusService);

  ngOnInit() {
    this.ngSelectDropdownScrollGuard.startWatching();
    this.ngSelectSearchFocusService.startWatching();
    this.timesheetStatusService.fetchForAuthenticatedSession();
    this.applyPendingEncodedUpdateDeepLink();

    // Monitor navigation to ensure proper theme handling
    this.routerSubscription = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      // If a race still landed on login/dashboard, recover the encoded deep link.
      this.applyPendingEncodedUpdateDeepLink();

      const url = event.urlAfterRedirects;
      this.handleRouteChange(url);

      if (!this.isPublicPage(url) && this.hasActiveSession()) {
        this.timesheetStatusService.ensureStatus().subscribe();
      }
    });
  }

  ngOnDestroy() {
    this.ngSelectDropdownScrollGuard.stopWatching();
    this.ngSelectSearchFocusService.stopWatching();
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Navigate to a rewritten email deep link saved by the capture-phase hash fix
   * (or rewrite now if `%23update|reply` is still present).
   */
  private applyPendingEncodedUpdateDeepLink(): void {
    const pending = consumePendingEncodedUpdateDeepLink() || consumeEncodedUpdateDeepLinkFix();
    if (!pending) {
      return;
    }
    const current = this.router.url || '';
    if (current === pending || current.startsWith(pending.split('#')[0])) {
      // Already on the target route (fragment scroll handled by updates page).
      return;
    }
    void this.router.navigateByUrl(pending, { replaceUrl: true });
  }

  private handleRouteChange(url: string) {
    // Check if we're on a page that should NOT have themes applied
    const isPublicPage = this.isPublicPage(url);

    if (isPublicPage) {
      // Clear theme-background-active class for public pages (login, forgot password)
      this.clearThemeForPublicPages();
    } else {
      // For protected pages, ensure theme is properly restored if it should be active
      this.restoreThemeForProtectedPages();
    }

    // Handle sidebar visibility based on route data
    this.handleSidebarVisibility();
  }

  private isPublicPage(url: string): boolean {
    const publicRoutes = ['/login', '/forgotpassword', '/error-screen', '/watch', '/privacy', '/login-with-otp'];
    return publicRoutes.some(route => url.includes(route));
  }

  private hasActiveSession(): boolean {
    return this.authService.isLoggedIn() || this.timesheetStatusService.hasAuthSession();
  }

  private clearThemeForPublicPages() {
    // Remove theme class and background from body for public pages
    const body = document.body;
    if (body.classList.contains('theme-background-active')) {
      body.classList.remove('theme-background-active');
      body.style.backgroundImage = '';
      console.log('Theme cleared for public page');
    }
  }

  private restoreThemeForProtectedPages() {
    // Check if theme should be active and restore it for protected pages
    const savedTheme = localStorage.getItem('selectedBackgroundTheme');
    const isThemeActive = localStorage.getItem('theme-background-active') === 'true';

    if (savedTheme && isThemeActive) {
      const body = document.body;
      if (!body.classList.contains('theme-background-active')) {
        // Restore theme for protected pages
        body.style.backgroundImage = `url(${savedTheme})`;
        body.style.backgroundSize = 'cover';
        body.style.backgroundPosition = 'center';
        body.style.backgroundRepeat = 'no-repeat';
        body.style.backgroundAttachment = 'fixed';
        body.classList.add('theme-background-active');
        console.log('Theme restored for protected page:', savedTheme);
      }
    }
  }

  handleChatbotAction(event: { action: string, data?: any }) {
    console.log('Chatbot action requested:', event);

    switch (event.action) {
      case 'open_task_form':
        // Emit global event to open task form on the current page
        this.emitGlobalEvent('openAddTaskForm', event.data);
        break;

      case 'open_project_form':
        // Emit global event to open project form on the current page
        this.emitGlobalEvent('openAddProjectForm', event.data);
        break;

      case 'open_add_project_form':
        // Emit global event to open add project form
        this.emitGlobalEvent('openAddProjectForm', event.data);
        break;

      case 'open_add_task_form':
        // Emit global event to open add task form
        this.emitGlobalEvent('openAddTaskForm', event.data);
        break;

      case 'search_and_open_project':
        // Emit global event to search and open project
        this.emitGlobalEvent('searchAndOpenProject', event.data);
        break;

      case 'search_and_open_project_by_name':
        // Emit global event to search and open project by name
        this.emitGlobalEvent('searchAndOpenProject', event.data.projectName);
        break;

      case 'navigate_and_search_project':
        // This will be handled by the navigate_and_search_project action in chatbot
        // The navigation happens first, then search_and_open_project is triggered
        break;

      default:
        console.log('Unknown chatbot action:', event.action);
    }
  }

  private emitGlobalEvent(eventName: string, data?: any) {
    // Create and dispatch a custom event that can be listened to by other components
    const customEvent = new CustomEvent(eventName, {
      detail: data,
      bubbles: true
    });
    document.dispatchEvent(customEvent);
  }

  private handleSidebarVisibility() {
    // Get current route data to check for hideSidebar property
    const currentRoute = this.router.routerState.snapshot.root;
    const routeData = this.getRouteData(currentRoute);
    
    const body = document.body;
    
    if (routeData && routeData['hideSidebar']) {
      // Hide sidebar for this route
      body.classList.add('route-hide-sidebar');
    } else {
      // Show sidebar for this route
      body.classList.remove('route-hide-sidebar');
    }
  }

  private getRouteData(route: any): any {
    if (route.data) {
      return route.data;
    }
    
    if (route.children) {
      for (const child of route.children) {
        const data = this.getRouteData(child);
        if (data) {
          return data;
        }
      }
    }
    
    return null;
  }
}
